import { toast } from "sonner";

import { doctorApi, doctorKeys, type CancelBookingResponse } from "@/lib/api/doctorApi";
import { log } from "@/lib/logger";
import { useMutation, useQueryClient } from "@/lib/query";

function messageFor(error: unknown): string {
  if (error instanceof Error && error.message) return error.message;
  return "We couldn't cancel this booking. Please try again.";
}

/**
 * Cancels a booking and refreshes the lists that classify it. Both the bookings
 * list and the dashboard counters are revalidated, so the appointment moves
 * from Booked to Cancelled without a manual reload.
 */
export function useCancelBooking() {
  const queryClient = useQueryClient();

  return useMutation<CancelBookingResponse, string>({
    mutationFn: (bookingId) => doctorApi.cancelBooking(bookingId),
    onSuccess: (data, bookingId) => {
      log.info("api", "booking.cancelled", { bookingId });
      void queryClient.invalidateQueries({ queryKey: doctorKeys.bookings });
      void queryClient.invalidateQueries({ queryKey: doctorKeys.stats });
      toast.success(data?.message ?? "Booking cancelled.");
    },
    onError: (error, bookingId) => {
      log.warn("api", "booking.cancelFailed", { bookingId, msg: messageFor(error) });
      toast.error(messageFor(error));
    },
  });
}
